class QueryGraphql {
  #apiVersion = '2024-10';
  #apiToken = '601838204b33f602c72b88e52f8652bc';
  #graphqlUrl = `https://aldkitchen.myshopify.com/api/${this.#apiVersion}/graphql`;

  async query(body) {
    const response = await fetch(this.#graphqlUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.#apiToken,
      },
      body: JSON.stringify(body),
    });

    if (response.status === 404) return null;
    return response.json();
  }
}

class ShopifyRecentlyViewed {
  constructor() {
    this.config = {
      howManyToStoreInMemory: 10,
      onComplete: null,
    };
    this.cookieName = 'shopify_recently_viewed';
    this.cookieConfiguration = {
      expires: 90,
      path: '/',
      domain: window.location.hostname,
    };

    this.#recordRecentlyViewed();
  }

  #cookieRead() {
    const cookieValue = getCookie(this.cookieName);
    let recentlyViewed = [];

    if (cookieValue) {
      recentlyViewed = cookieValue.split(' ');
    }
    return recentlyViewed;
  }

  #cookieWrite(recentlyViewed) {
    setCookie(this.cookieName, recentlyViewed.join(' '), this.cookieConfiguration);
  }

  #recordRecentlyViewed() {
    const recentlyViewed = this.#cookieRead();

    if (window.location.pathname.includes('/products/')) {
      const productHandle = window.location.pathname.match(/\/products\/([a-z0-9\-]+)/)[1];
      const position = recentlyViewed.findIndex(handle => handle === productHandle);

      if (position === -1) {
        recentlyViewed.unshift(productHandle);
        recentlyViewed.splice(this.config.howManyToStoreInMemory);
      } else {
        recentlyViewed.splice(position, 1);
        recentlyViewed.unshift(productHandle);
      }

      this.#cookieWrite(recentlyViewed);
    }
  }

  async getRecentlyViewed() {
    const recentlyViewed = this.#cookieRead();

    if (!recentlyViewed.length) return [];

    const responses = await Promise.all(
      recentlyViewed.map(handle =>
        fetch(`/products/${handle}?view=json`).then(response => (response.status === 404 ? null : response.json())),
      ),
    );

    return responses.filter(response => response);
  }
}

class EventEmitter {
  #events = new Map();

  subscribe(event, callback) {
    if (!this.#events.has(event)) {
      this.#events.set(event, []);
    }
    this.#events.get(event).push(callback);
  }

  emit(event, data) {
    const callbacks = this.#events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  listeners(event) {
    const callbacks = this.#events.get(event);
    if (!callbacks) return [];
    return callbacks;
  }

  unsubscribe(event, callback) {
    const callbacks = this.#events.get(event);
    if (!callbacks) return;
    this.#events.set(
      event,
      callbacks.filter(cb => cb !== callback),
    );
  }

  unsubscribeAll(event) {
    if (event) {
      this.#events.delete(event);
    } else {
      this.#events.clear();
    }
  }
}

class ShopifyService {
  constructor(queryGraphql) {
    this.queryGraphql = queryGraphql;
    this.root = Shopify.routes.root;
  }

  fetchProduct(handle) {
    return fetch(`${this.root}products/${handle}?view=json`).then(response => (response.status === 404 ? null : response.json()));
  }

  fetchCollection(handle) {
    return fetch(`${this.root}collections/${handle}?view=json`).then(response => (response.status === 404 ? null : response.json()));
  }

  fetchCollections(ids) {
    const query = `
      {
        nodes(ids: ${JSON.stringify(ids)}) {
          ...on Collection {
            id,
            handle,
            title,
            products (first: 250) {
              edges {
                node {
                  id,
                  handle,
                  title,
                  variants (first: 100) {
                    edges {
                      node {
                        id,
                        availableForSale,
                        selectedOptions {
                          name,
                          value
                        },
                        title,
                        price {
                          amount,
                          currencyCode
                        },
                        sku,
                        image {
                          originalSrc
                        },
                        arrives: metafield(namespace: "aldkitchen", key: "arrives") {
                          value
                        },
                        restock: metafield(namespace: "aldkitchen", key: "restocking_date") {
                          value
                        },
                        ships_from: metafield(namespace: "aldkitchen", key: "ships_from") {
                          value
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.queryGraphql.query({ query });
  }

  fetchProducts(ids) {
    const query = `
      {
        nodes(ids: ${JSON.stringify(ids)}) {
          ...on Product {
            id,
            handle,
            title,
            free_gift_gid: metafield(namespace: "cart", key: "free_gift") {
              value
            }
            collections (first: 100) {
              edges {
                node {
                  id,
                  handle
                }
              }
            },
            variants (first: 100) {
              edges {
                node {
                  id,
                  availableForSale,
                  title,
                  price {
                    amount,
                    currencyCode
                  },
                  sku,
                  selectedOptions {
                    name,
                    value
                  },
                  image {
                    originalSrc
                  },
                  arrives: metafield(namespace: "aldkitchen", key: "arrives") {
                    value
                  },
                  restock: metafield(namespace: "aldkitchen", key: "restocking_date") {
                    value
                  },
                  ships_from: metafield(namespace: "aldkitchen", key: "ships_from") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    `;
    return this.queryGraphql.query({ query });
  }

  fetchProductVariants(ids) {
    const query = `{
      nodes(ids: ${JSON.stringify(ids)}) {
        ...on ProductVariant {
          id,
          title,
          availableForSale,
          price {
            amount,
            currencyCode
          },
          sku,
          selectedOptions {
            name,
            value
          },
          image {
            originalSrc
          },
          product {
            id,
            handle,
            title,
            free_gift_gid: metafield(namespace: "cart", key: "free_gift") {
              value
            },
            featuredImage {
              originalSrc
            },
            collections (first: 100) {
              edges {
                node {
                  id,
                  handle
                }
              }
            },
          },
          arrives: metafield(namespace: "aldkitchen", key: "arrives") {
            value
          },
          restock: metafield(namespace: "aldkitchen", key: "restocking_date") {
            value
          },
          ships_from: metafield(namespace: "aldkitchen", key: "ships_from") {
            value
          }
        }
      }
    }`;
    return this.queryGraphql.query({ query });
  }
}

class CartApiService {
  #apiRoutes = {
    get: Shopify.routes.cart_url,
    clear: Shopify.routes.cart_clear_url,
    add: Shopify.routes.cart_add_url,
    change: Shopify.routes.cart_change_url,
    update: Shopify.routes.cart_update_url,
  };

  #fetchConfig(method = 'POST', type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
      },
    };
  }

  #sendRequest(url, body = {}) {
    const options = {
      ...this.#fetchConfig(url === this.#apiRoutes.get ? 'GET' : 'POST'),
      body: JSON.stringify(body),
    };
    return fetch(url, options).then(response => response.json());
  }

  get(body) {
    return this.#sendRequest(this.#apiRoutes.get, body);
  }

  clear(body) {
    return this.#sendRequest(this.#apiRoutes.clear, body);
  }

  add(body) {
    return this.#sendRequest(this.#apiRoutes.add, body);
  }

  change(body) {
    return this.#sendRequest(this.#apiRoutes.change, body);
  }

  update(body) {
    return this.#sendRequest(this.#apiRoutes.update, body);
  }
}

class DiscountApiService {
  CART_DISCOUNT_CODES_UPDATE = `
    mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
      cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
        cart {
          id
          discountCodes {
            code
            applicable
          }
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  APPLIED_DISCOUNTS = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) {
        discountCodes {
          code
          applicable
        }
      }
    }
  `;

  constructor(queryGraphql) {
    this.queryGraphql = queryGraphql;
  }

  async fetchAppliedDiscounts() {
    try {
      const cartDiscountCodes = await this.fetchAppliedDiscountCodes();
      const appliedDiscounts = [];

      for (const discount of cartDiscountCodes) {
        const discountInfo = await this.fetchDiscountInfoByCode(discount.code);

        if (discountInfo) {
          appliedDiscounts.push({ ...discountInfo, code: discount.code });
        }
      }

      return appliedDiscounts;
    } catch (error) {
      return [];
    }
  }

  async fetchAppliedDiscountCodes() {
    try {
      const cartId = this.#getCartId();
      const response = await this.queryGraphql.query({
        query: this.APPLIED_DISCOUNTS,
        variables: { cartId },
      });

      return response.data?.cart?.discountCodes || [];
    } catch (error) {
      return [];
    }
  }

  #getCartId() {
    const cartCookie = getCookie('cart');
    return `gid://shopify/Cart/${cartCookie}`;
  }

  async fetchDiscountInfoByCode(discountCode) {
    try {
      const response = await fetch(
        `https://ald-custom.digitaluniverse.app/api/discount-info?code=${encodeURIComponent(discountCode)}`,
        {
          headers: {
            'X-Shopify-Shop-Domain': Shopify.shop,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  apply(discountCodes) {
    return this.queryGraphql.query({
      query: this.CART_DISCOUNT_CODES_UPDATE,
      variables: {
        cartId: `gid://shopify/Cart/${getCookie('cart')}`,
        discountCodes: discountCodes.filter(Boolean),
      },
    });
  }
}

class CartManager {
  #events = {
    updated: 'ald:cart:updated',
  };
  #cachedCart = null;

  constructor(apiService, eventEmitter, freeGiftManager) {
    this.apiService = apiService;
    this.eventEmitter = eventEmitter;
    this.freeGiftManager = freeGiftManager;

    this.#initializeCache();
  }

  async #initializeCache() {
    try {
      this.#cachedCart = await this.apiService.get();
    } catch (error) {
      console.error(error)
    }
  }

  get() {
    return this.#cachedCart ? Promise.resolve(this.#cachedCart) : this.refresh();
  }

  refresh() {
    return this.apiService.get().then(cart => {
      this.#cachedCart = cart;
      return cart;
    });
  }

  clear(body) {
    return this.#performAction('clear', body);
  }

  add(body) {
    return this.#performAction('add', body);
  }

  update(body) {
    return this.#performAction('update', body);
  }

  setAttributes(attributes) {
    return this.update({ attributes });
  }

  setNote(note) {
    return this.update({ note });
  }

  onUpdated(callback) {
    this.eventEmitter.subscribe(this.#events.updated, callback);
  }

  async #performAction(action, body) {
    const mutatedBody = clone(body);

    if (['add', 'update'].includes(action)) {
      await this.freeGiftManager.addFreeGiftsToCart(mutatedBody, this.get.bind(this));
    }

    return this.apiService[action](mutatedBody)
      .then(data => {
        if (data.errors) {
          throw new Error(data.errors);
        }

        this.refresh().then(cart => this.#triggerUpdated(cart));

        return data;
      })
      .catch(error => console.error(error));
  }

  #triggerUpdated(cart) {
    this.eventEmitter.emit(this.#events.updated, cart);
  }
}

class DiscountManager {
  #appliedDiscounts = {};
  #discountCache = {};
  #events = {
    updated: 'ald:discount:updated',
  };

  constructor(apiService, eventEmitter) {
    this.apiService = apiService;
    this.eventEmitter = eventEmitter;

    this.#initializeAppliedDiscounts().then();
  }

  async #initializeAppliedDiscounts() {
    try {
      const discounts = await this.apiService.fetchAppliedDiscounts();
      this.#appliedDiscounts = this.#mapDiscountsToApplied(discounts);
      this.#discountCache = { ...this.#discountCache, ...this.#appliedDiscounts };
      this.eventEmitter.emit(this.#events.updated, Object.keys(this.#appliedDiscounts));
    } catch (error) {
      console.error(error);
    }
  }

  get appliedDiscountCodes() {
    return Object.keys(this.#appliedDiscounts);
  }

  #mapDiscountsToApplied(discounts) {
    return discounts.reduce((acc, discount) => {
      const discountInfo = this.#extractDiscountInfo(discount);
      acc[discount.code] = discountInfo;
      return acc;
    }, {});
  }

  #extractDiscountInfo(discount) {
    const {
      codeDiscountNodeByCode: { codeDiscount },
    } = discount;
    const collections = codeDiscount.customerGets.items.collections.nodes.map(
      collection => +collection.id.replace('gid://shopify/Collection/', ''),
    );

    return {
      type: codeDiscount.discountClass,
      appliesOnOneTimePurchase: codeDiscount.customerGets.appliesOnOneTimePurchase,
      appliesOnSubscription: codeDiscount.customerGets.appliesOnSubscription,
      value: {
        type: 'percentage',
        value: codeDiscount.customerGets.value.percentage * 100,
      },
      collections,
    };
  }

  apply(codes) {
    const newCodes = [...Object.keys(this.#appliedDiscounts), ...codes];
    return this.#performAction(newCodes, this.#events.updated);
  }

  clear(codes) {
    const newCodes = Object.keys(this.#appliedDiscounts).filter(code => !codes.includes(code));
    return this.#performAction(newCodes, this.#events.updated);
  }

  onUpdated(callback) {
    this.eventEmitter.subscribe(this.#events.updated, callback);
  }

  getDiscountInfoByCode(code) {
    return this.#discountCache[code] || this.#appliedDiscounts[code];
  }

  async #loadDiscountInfo(code) {
    if (this.#discountCache[code]) {
      return this.#discountCache[code];
    }

    const rawDiscount = await this.apiService.fetchDiscountInfoByCode(code);
    
    if (rawDiscount) {
      const processedDiscount = this.#extractDiscountInfo(rawDiscount);
      this.#discountCache[code] = processedDiscount;
      return processedDiscount;
    }

    return null;
  }

  async checkDiscountEligibility(product, discountCode, appliesOnSubscription = false) {
    const discount = await this.#loadDiscountInfo(discountCode);

    if (!discount || !product) return false;

    const isEligiblePurchaseType = appliesOnSubscription
      ? discount.appliesOnSubscription
      : discount.appliesOnOneTimePurchase;

      return (
      this.#isOrderDiscount(discount) ||
      (isEligiblePurchaseType && this.#isProductDiscountApplicable(product, discount))
    );
  }

  getPriceWithDiscount(price, discountCode) {
    const discount = this.getDiscountInfoByCode(discountCode);

    if (!discount || !discount.value || !discount.value.type) return price;

    const { type, value } = discount.value;

    if (type === 'percentage') {
      return Math.round(price * (1 - value / 100));
    }

    return price;
  }

  isProductEligibleForDiscount(product, discountCode, appliesOnSubscription = false) {
    const discount = this.getDiscountInfoByCode(discountCode);

    if (!discount || !product) return false;

    const isEligiblePurchaseType = appliesOnSubscription
      ? discount.appliesOnSubscription
      : discount.appliesOnOneTimePurchase;

    return (
      this.#isOrderDiscount(discount) ||
      (isEligiblePurchaseType && this.#isProductDiscountApplicable(product, discount))
    );
  }

  #isOrderDiscount(discount) {
    return discount.type === 'ORDER';
  }

  #isProductDiscountApplicable(product, discount) {
    const productCollectionIds = product.collections?.map(collection => collection.id) || [];
    const isInDiscountedCollection = discount.collections?.some(id => productCollectionIds.includes(id));
    const isDirectProductMatch = discount.products?.includes(product.id);

    return isInDiscountedCollection || isDirectProductMatch;
  }

  #performAction(discountCodes) {
    return this.apiService
      .apply(discountCodes)
      .then(() => this.#initializeAppliedDiscounts())
      .catch(error => console.error(error));
  }
}

class FreeGiftManager {
  async addFreeGiftsToCart(body, fetchCart) {
    const field = this.getFieldType(body);

    if (field === 'items') {
      this.addFreeGiftsToItems(body);
    } else if (field === 'updates') {
      await this.updateFreeGiftsInCart(body, fetchCart);
    }
  }

  addFreeGiftsToItems(body) {
    const items = [...body.items];

    items.forEach(item => {
      const freeGiftId = this.extractFreeGiftId(item);

      if (freeGiftId) {
        body.items.push({
          id: freeGiftId,
          quantity: item.quantity,
          properties: {
            _free_gift_for: item.id,
            'Free gift for': item.id,
          },
        });
      }
    });
  }

  async updateFreeGiftsInCart(body, fetchCart) {
    const cart = await fetchCart();
    const updates = { ...body.updates };

    Object.keys(updates).forEach(key => {
      const relatedItem = cart.items.find(item => item.key === key);
      const freeGiftItem = cart.items.find(item => item.properties._free_gift_for?.includes(relatedItem.id));

      if (relatedItem?.properties?.[`_should_free_gift_${freeGiftItem?.id}`]) {
        body.updates[freeGiftItem.key] = updates[key];
      }
    });
  }

  extractFreeGiftId(item) {
    if (!item.properties) return null;
    const k = '_should_free_gift_';
    return Object.keys(item.properties)
      .find(key => key.includes(k))
      ?.replace(k, '');
  }

  getFieldType(body) {
    return body.items ? 'items' : body.updates ? 'updates' : null;
  }
}

(() => {
  const queryGraphql = new QueryGraphql();
  const cartApiService = new CartApiService();
  const discountApiService = new DiscountApiService(queryGraphql);
  const eventEmitter = new EventEmitter();
  const shopifyService = new ShopifyService(queryGraphql);
  const discountManager = new DiscountManager(discountApiService, eventEmitter);
  const freeGiftManager = new FreeGiftManager();
  const cartManager = new CartManager(cartApiService, eventEmitter, freeGiftManager);
  const recentlyViewed = new ShopifyRecentlyViewed();

  setupGlobalVariables();
  setupEventListeners();

  if (location.pathname === '/policies/terms-of-service') {
    createNavOnTermPage();
  }

  const header = document.getElementById('shopify-section-header');
  const resizeObserver = new ResizeObserver(() => {
    updateHeaderBottomPosition();
  });
  if (header) {
    resizeObserver.observe(header);
  }

  updateHeaderBottomPosition();

  function updateHeaderBottomPosition() {
    if (header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(header.getBoundingClientRect().bottom)}px`,
      );
    }
  }

  function setupEventListeners() {
    window.addEventListener('scroll', showFooter, { passive: true });

    document.addEventListener('click', (e) => {
      const toggle = e.target.closest('.disclosure');
      if (!toggle) return;

      e.preventDefault();
      const key = toggle.dataset.disclosure;
      const content = document.querySelector(`[data-disclosure="${key}"]:not(.disclosure)`);
      if (!content) return;

      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
      const newState = !isExpanded;

      toggle.setAttribute('aria-expanded', newState);
      content.classList.toggle('hidden', !newState);
    });
  }

  function setupGlobalVariables() {
    window.Shopify = window.Shopify || {};
    window.monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    window.isIOS = (() =>
      ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
      (navigator.userAgent.includes('Mac') && 'ontouchend' in document))();
    window.showProductsByVoltage = showProductsByVoltage;

    Date.prototype.mmdd = function () {
      const month = window.monthNames[this.getMonth()];
      const day = String(this.getDate());
      return `${month} ${day}`;
    };

    Shopify.discount = discountManager;
    Shopify.cart = cartManager;
    Shopify.shopifyService = shopifyService;
    Shopify.recentlyViewed = recentlyViewed;
    Shopify.eventEmitter = eventEmitter;

    // TODO: переделать на класс
    Shopify.bind = function (fn, scope) {
      return function () {
        return fn.apply(scope, arguments);
      };
    };

    Shopify.setSelectorByValue = function (selector, value) {
      for (var i = 0, count = selector.options.length; i < count; i++) {
        var option = selector.options[i];

        if (value == option.value || value == option.innerHTML) {
          selector.selectedIndex = i;
          return i;
        }
      }
    };

    Shopify.addListener = function (target, eventName, callback) {
      target.addEventListener
        ? target.addEventListener(eventName, callback, false)
        : target.attachEvent('on' + eventName, callback);
    };

    Shopify.postLink = function (path, options) {
      options = options || {};
      var method = options['method'] || 'post';
      var params = options['parameters'] || {};
      var form = document.createElement('form');
      form.setAttribute('method', method);
      form.setAttribute('action', path);

      for (var key in params) {
        var hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
      }

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    };

    Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
      this.countryEl = document.getElementById(country_domid);
      this.provinceEl = document.getElementById(province_domid);
      this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

      Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
      this.initCountry();
      this.initProvince();
    };

    Shopify.CountryProvinceSelector.prototype = {
      initCountry: function () {
        var value = this.countryEl.getAttribute('data-default');
        Shopify.setSelectorByValue(this.countryEl, value);
        this.countryHandler();
      },
      initProvince: function () {
        var value = this.provinceEl.getAttribute('data-default');

        if (value && this.provinceEl.options.length > 0) {
          Shopify.setSelectorByValue(this.provinceEl, value);
        }
      },
      countryHandler: function (e) {
        var opt = this.countryEl.options[this.countryEl.selectedIndex];
        var raw = opt?.getAttribute('data-provinces') || '[]';
        var provinces = JSON.parse(raw);
        this.clearOptions(this.provinceEl);

        if (provinces && provinces.length == 0) {
          this.provinceContainer.style.display = 'none';
        } else {
          for (var i = 0; i < provinces.length; i++) {
            var opt = document.createElement('option');
            opt.value = provinces[i][0];
            opt.innerHTML = provinces[i][1];
            this.provinceEl.appendChild(opt);
          }

          this.provinceContainer.style.display = '';
        }
      },
      clearOptions: function (selector) {
        while (selector.firstChild) {
          selector.removeChild(selector.firstChild);
        }
      },
      setOptions: function (selector, values) {
        for (var i = 0, count = values.length; i < values.length; i++) {
          var opt = document.createElement('option');
          opt.value = values[i];
          opt.innerHTML = values[i];
          selector.appendChild(opt);
        }
      },
    };
  }

  function showFooter() {
    const footer = document.querySelector('footer.footer');
    footer && footer.setAttribute('style', 'opacity: 1');
  }

  function createNavOnTermPage() {
    const container = document.querySelector('.shopify-policy__body');
    const content = document.querySelector('.shopify-policy__body .rte');

    if (!container || !content) return;

    const allH2 = content.querySelectorAll('h2');

    if (!allH2.length) return;

    const nav = document.createElement('div');
    nav.classList.add('nav');

    allH2.forEach((item, index) => {
      item.id = `anchor_${index}`;
      item.setAttribute('style', 'scroll-margin-top: 17rem;');

      const link = document.createElement('a');
      link.href = `#${item.id}`;
      link.innerText = item.innerText;
      nav.appendChild(link);
    });

    container.setAttribute('style', 'display: flex; gap: 10rem;');
    container.prepend(nav);
  }

  function showProductsByVoltage() {
    const productItems = document.querySelectorAll('.collection-page__grid .grid__item');

    productItems.forEach(el => {
      const { v110, v220 } = Shopify.strings;

      const voltage = Shopify.currentCountry?.voltage ?? v110;
      const variantTitle = el.getAttribute('data-variant-title');

      if (!variantTitle?.includes(voltage === v110 ? v220 : v110)) {
        el.setAttribute('style', 'display: flex');
      }
    });
  }
})();
